import Dexie, { type Table } from 'dexie'
import type {
  Category,
  Contact,
  Device,
  DeviceImei,
  Expense,
  Media,
  OutboxOperation,
  Payment,
  PullCursor,
  Purchase,
  Sale,
} from './types'

/**
 * Account-isolated IndexedDB. One database per workspace so switching accounts
 * never mixes queues or caches and never discards pending data (plan section 8).
 */
export class WorkspaceDb extends Dexie {
  categories!: Table<Category, string>
  devices!: Table<Device, string>
  device_imeis!: Table<DeviceImei, string>
  purchases!: Table<Purchase, string>
  sales!: Table<Sale, string>
  payments!: Table<Payment, string>
  expenses!: Table<Expense, string>
  contacts!: Table<Contact, string>
  media!: Table<Media, string>
  outbox!: Table<OutboxOperation, string>
  pull_cursor!: Table<PullCursor, string>

  constructor(name: string) {
    super(name)
    this.version(1).stores({
      categories: 'id, workspace_id, name, seed_key, archived_at',
      devices: 'id, workspace_id, category_id, brand_code, status, updated_at',
      device_imeis: 'id, device_id, workspace_id, imei_normalized',
      purchases: 'id, workspace_id, device_id, purchased_at',
      sales: 'id, workspace_id, device_id, purchase_id, sold_at',
      payments: 'id, workspace_id, related_type, related_id, direction',
      expenses: 'id, workspace_id, spent_at',
      contacts: 'id, workspace_id, kind, name',
      media: 'id, workspace_id, device_id, lifecycle',
      outbox: 'operation_id, workspace_id, entity_id, created_at',
      pull_cursor: 'workspace_id',
    })
  }
}

const openDbs = new Map<string, WorkspaceDb>()

export function dbKey(workspaceId: string): string {
  return `mtp_${workspaceId}`
}

export function openWorkspaceDb(workspaceId: string): WorkspaceDb {
  const key = dbKey(workspaceId)
  let db = openDbs.get(key)
  if (!db) {
    db = new WorkspaceDb(key)
    openDbs.set(key, db)
  }
  return db
}

export async function closeWorkspaceDb(workspaceId: string): Promise<void> {
  const key = dbKey(workspaceId)
  const db = openDbs.get(key)
  if (db) {
    db.close()
    openDbs.delete(key)
  }
}

/** Test/maintenance helper: permanently delete a workspace's local database. */
export async function deleteWorkspaceDb(workspaceId: string): Promise<void> {
  await closeWorkspaceDb(workspaceId)
  await Dexie.delete(dbKey(workspaceId))
}
